import { useLocalMerge } from '../MergeConflicts/MergeConflicts';
import { useCallback } from 'react';
import { AlertType } from '../../graphql/schema/p4types';
import ConcurrencyUtils from '../../utils/ConcurrencyUtils';
import { EntityType } from '../../@types/entityTypes';
import { useP4ToastController } from '../../views/system/NotificationsArea';

/**
 * Process updated form data (Either due to initial loading of data, or due to a refetch for other reasons, such as a concurrency conflict that is being auto-merged.
 */
export const useProcessFormData = <T = any>({
  entity,
  params,
  name,
  fields,
  fieldValidation,
  initialize,
  values,
  baseValue,
}: {
  entity: EntityType;
  params: any;
  name: string;
  fields: { [key: string]: any };
  fieldValidation: Array<any>;
  initialize: Function;
  values: T;
  baseValue: T;
}) => {
  const { dispatchSimpleToast } = useP4ToastController();
  const localMerge = useLocalMerge({
    form: entity.type,
    entity: entity,
  });

  return useCallback(
    (newFormData, { isConcurrencyReload } = { isConcurrencyReload: false }) => {
      const formType = params?.uuid ? 'New' : params?.id;

      let data, error;
      switch (formType) {
        case 'New':
          data = newFormData.defaultRecord;
          error = newFormData.error;
          break;
        case 'Parameters':
          data = fields;
          error = null;
          break;
        default:
          data = newFormData.data || newFormData.defaultRecord;
          error = newFormData.error;
      }

      if (error) {
        const errorMessage = error.networkError?.result?.errors?.[0]?.message;
        dispatchSimpleToast(
          `${name} Error`,
          `Error loading data ${errorMessage ? errorMessage : error}`,
          AlertType.Error
        );
        return null;
      } else if (isConcurrencyReload) {
        const concurrencyUtils = new ConcurrencyUtils();
        const conflicts = concurrencyUtils.compareValues(newFormData.data, values);
        //Use local merge system rather than full merge conflicts capability (Since the full version triggers a save on confirm, but we want to just merge in where possible.
        localMerge(conflicts, baseValue);
        return data;
      }

      initialize(data, fieldValidation);
      return data;
    },
    [
      params?.uuid,
      params?.id,
      initialize,
      fieldValidation,
      fields,
      dispatchSimpleToast,
      name,
      values,
      localMerge,
      baseValue,
    ]
  );
};
